/**
 * Custom HTML Overlay Editor
 *
 * The authoring harness for a custom_html overlay: edit the html/css/js bodies
 * and the declared fields schema, manage referenced assets, mint the OBS token,
 * and preview the real sandbox page (events fired through the live pipeline).
 *
 * Route: /channels/:channelId/overlays/custom/:overlayId
 *
 * The overlay runs in the slice-4 sandbox page (a null-origin iframe on the
 * sandbox origin). Preview === live: the test-fire bar drives the same
 * emit_overlay_event() pipeline a real follow/cheer would, so what renders here
 * is exactly what renders in OBS. Preview reflects the SAVED bodies — save, then
 * reload the preview to see edits.
 *
 * Styling: synthwave design system (semantic tokens via .card/.btn/.input and
 * synth-* / state-* / text-* / surface-* roles). The preview checkerboard is an
 * intentional transparency-reveal surface (design-system.md §5).
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import {
  getOverlay,
  updateOverlay,
  regenerateToken,
  testFire,
  type Overlay,
  type FieldSpec,
  type OverlayWithToken,
} from '@/api/overlaysApi'
import {
  listAssets,
  uploadAsset,
  deleteAsset,
  type AssetInfo,
  type AssetKind,
} from '@/api/assetsApi'
import {
  Save,
  KeyRound,
  Copy,
  Check,
  Play,
  Plus,
  Trash2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Upload,
} from 'lucide-react'
import clsx from 'clsx'

// Test-fire presets per alert type, so the preview button "just works".
const FIRE_TYPES = ['follow', 'subscribe', 'cheer', 'raid'] as const
type FireType = (typeof FIRE_TYPES)[number]

const FIRE_PRESETS: Record<FireType, { capability: string; fields: Record<string, unknown> }> = {
  follow: { capability: 'core.follow', fields: { follower_name: 'TestViewer' } },
  subscribe: { capability: 'core.subscribe', fields: { viewer_name: 'TestViewer', tier: '1000' } },
  cheer: { capability: 'core.cheer', fields: { viewer_name: 'TestViewer', bits: 500, anonymous: false } },
  raid: { capability: 'core.raid', fields: { from_name: 'TestRaider', viewers: 42 } },
}

const CODE_TABS = ['html', 'css', 'js'] as const
type CodeTab = (typeof CODE_TABS)[number]

const FIELD_TYPES: FieldSpec['type'][] = ['text', 'number', 'color', 'boolean']

/** Coerce a default typed into the text input back to the field's declared type. */
function coerceDefault(type: FieldSpec['type'], raw: string): unknown {
  if (type === 'number') {
    const n = Number(raw)
    return raw.trim() === '' || Number.isNaN(n) ? raw : n
  }
  if (type === 'boolean') return raw === 'true'
  return raw
}

/** A read-only field with a copy button — used for the OBS URL + token. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard may be unavailable; value is still selectable
    }
  }
  return (
    <div>
      <label className="block text-sm text-text-secondary mb-2">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="input font-mono text-sm flex-1"
        />
        <button type="button" onClick={copy} className="btn-secondary flex items-center gap-2 shrink-0">
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

/** One-time token + OBS URL modal (shown after minting / regenerating). */
function TokenModal({ result, onClose }: { result: OverlayWithToken; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-overlay rounded-xl border border-border p-6 w-full max-w-lg">
        <h2 className="text-xl font-semibold text-text-primary mb-2">OBS URL ready</h2>
        <div className="mb-4 p-3 bg-state-warning-bg border border-state-warning rounded-lg flex items-start gap-2 text-state-warning text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>
            This is the only time the token is shown. Copy the URL into your OBS browser source now;
            if you lose it, regenerate a new one (the old URL stops working).
          </span>
        </div>
        <div className="space-y-4">
          <CopyField label="OBS browser source URL" value={result.overlay_url} />
          <CopyField label="Token" value={result.token} />
        </div>
        <div className="flex justify-end mt-6">
          <button type="button" onClick={onClose} className="btn-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

/** Asset list with upload + copy-URL — custom HTML references assets by URL. */
function AssetPanel({ channelId }: { channelId: number }) {
  const { success, error } = useToast()
  const [assets, setAssets] = useState<AssetInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [kind, setKind] = useState<AssetKind>('image')
  const [uploading, setUploading] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setAssets(await listAssets(channelId))
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to load assets'))
    } finally {
      setLoading(false)
    }
  }, [channelId, error])

  useEffect(() => {
    load()
  }, [load])

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (!file) return
    setUploading(true)
    try {
      await uploadAsset(channelId, kind, file)
      success(`Uploaded ${file.name}`)
      await load()
    } catch (err) {
      error(apiErrorMessage(err, 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  const copyUrl = async (a: AssetInfo) => {
    try {
      await navigator.clipboard.writeText(a.url)
      setCopiedId(a.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      error('Could not copy to clipboard.')
    }
  }

  const remove = async (a: AssetInfo) => {
    if (!confirm(`Delete "${a.filename}"? Overlays referencing it will break.`)) return
    try {
      await deleteAsset(a.id)
      setAssets((prev) => prev.filter((x) => x.id !== a.id))
      success(`Deleted ${a.filename}`)
    } catch (err) {
      error(apiErrorMessage(err, 'Delete failed'))
    }
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-text-primary">Assets</h3>
        <div className="flex items-center gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as AssetKind)}
            className="input py-1.5 text-sm w-auto"
          >
            <option value="image">image</option>
            <option value="sound">sound</option>
            <option value="font">font</option>
          </select>
          <label className="btn-secondary flex items-center gap-2 cursor-pointer text-sm">
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            Upload
            <input type="file" className="hidden" onChange={onFile} disabled={uploading} />
          </label>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : assets.length === 0 ? (
        <p className="text-sm text-text-muted">
          No assets yet. Upload images / sounds / fonts, then reference them by URL in your code.
        </p>
      ) : (
        <ul className="space-y-2">
          {assets.map((a) => (
            <li key={a.id} className="flex items-center gap-3 text-sm">
              <span className="px-2 py-0.5 rounded-full bg-surface-overlay text-text-secondary text-xs shrink-0">
                {a.kind}
              </span>
              <span className="text-text-secondary truncate flex-1" title={a.filename}>
                {a.filename || a.id}
              </span>
              <button
                onClick={() => copyUrl(a)}
                className="text-text-secondary hover:text-synth-magenta flex items-center gap-1 shrink-0"
                title="Copy URL"
              >
                {copiedId === a.id ? <Check size={14} /> : <Copy size={14} />}
                URL
              </button>
              <button
                onClick={() => remove(a)}
                className="text-text-secondary hover:text-state-danger shrink-0"
                title="Delete asset"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function CustomOverlayEditor() {
  const navigate = useNavigate()
  const { channelId, overlayId } = useParams<{ channelId: string; overlayId: string }>()
  const chId = Number(channelId)

  const [overlay, setOverlay] = useState<Overlay | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [html, setHtml] = useState('')
  const [css, setCss] = useState('')
  const [js, setJs] = useState('')
  const [fields, setFields] = useState<FieldSpec[]>([])
  const [activeTab, setActiveTab] = useState<CodeTab>('html')

  const [saving, setSaving] = useState(false)
  const [minting, setMinting] = useState(false)
  const [firing, setFiring] = useState(false)
  const [fireType, setFireType] = useState<FireType>('follow')

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewKey, setPreviewKey] = useState(0)
  const [tokenResult, setTokenResult] = useState<OverlayWithToken | null>(null)

  const { success, error } = useToast()

  const load = useCallback(async () => {
    if (!overlayId) return
    try {
      const ov = await getOverlay(overlayId)
      setOverlay(ov)
      setName(ov.name)
      setEnabled(ov.enabled)
      setHtml(ov.html_body ?? '')
      setCss(ov.css_body ?? '')
      setJs(ov.js_body ?? '')
      setFields(Array.isArray(ov.fields_schema) ? ov.fields_schema : [])
      setLoadError(null)
    } catch (err) {
      // Fatal load failure stays INLINE, not a toast.
      setLoadError(apiErrorMessage(err, 'Failed to load overlay'))
    } finally {
      setIsLoading(false)
    }
  }, [overlayId])

  useEffect(() => {
    load()
  }, [load])

  const save = async () => {
    if (!overlay) return
    setSaving(true)
    try {
      const updated = await updateOverlay(overlay.id, {
        name,
        enabled,
        html_body: html,
        css_body: css,
        js_body: js,
        fields_schema: fields,
      })
      setOverlay(updated)
      success('Saved')
      // Reflect saved bodies in the preview, if one is loaded.
      if (previewUrl) setPreviewKey((k) => k + 1)
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  const mintToken = async () => {
    if (!overlay) return
    setMinting(true)
    try {
      const result = await regenerateToken(overlay.id)
      setTokenResult(result)
      setPreviewUrl(result.overlay_url)
      setPreviewKey((k) => k + 1)
      // has_token is now true; reflect without a full reload.
      setOverlay((o) => (o ? { ...o, has_token: true } : o))
    } catch (err) {
      // No success toast: the TokenModal is the report.
      error(apiErrorMessage(err, 'Failed to mint token'))
    } finally {
      setMinting(false)
    }
  }

  const fire = async () => {
    if (!overlay) return
    const preset = FIRE_PRESETS[fireType]
    setFiring(true)
    try {
      const result = await testFire(overlay.id, preset.capability, preset.fields)
      if (result.status === 'fired') {
        success('Test fired — watch the preview')
      } else {
        error(result.detail || 'Nothing rendered (is this overlay in the alert slot?)')
      }
    } catch (err) {
      error(apiErrorMessage(err, 'Test-fire failed'))
    } finally {
      setFiring(false)
    }
  }

  // --- fields schema editing ---
  const addField = () => {
    setFields((prev) => [...prev, { key: '', type: 'text', label: '', default: '' }])
  }
  const updateField = (idx: number, patch: Partial<FieldSpec>) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)))
  }
  const removeField = (idx: number) => {
    setFields((prev) => prev.filter((_, i) => i !== idx))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta" />
      </div>
    )
  }

  if (loadError || !overlay) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(`/channels/${chId}/overlays`)}
          className="text-sm text-text-secondary hover:text-text-primary"
        >
          ← Back to overlays
        </button>
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{loadError || 'Overlay not found'}</span>
        </div>
      </div>
    )
  }

  const codeValue = activeTab === 'html' ? html : activeTab === 'css' ? css : js
  const setCodeValue = (v: string) =>
    activeTab === 'html' ? setHtml(v) : activeTab === 'css' ? setCss(v) : setJs(v)

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate(`/channels/${chId}/overlays`)}
          className="text-sm text-text-secondary hover:text-text-primary mb-2"
        >
          ← Back to overlays
        </button>
        <h2 className="text-2xl font-bold text-text-primary">{overlay.name}</h2>
        <p className="text-text-secondary">
          custom HTML overlay · slot {overlay.slot}
        </p>
      </div>

      {/* Overlay-level settings */}
      <div className="card space-y-4">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm text-text-secondary mb-2">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer pb-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 accent-synth-magenta"
            />
            <span className="text-sm text-text-secondary">Enabled</span>
          </label>
          <button onClick={save} className="btn-primary flex items-center gap-2" disabled={saving}>
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save
          </button>
        </div>
      </div>

      {/* Code editors */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-text-primary">Code</h3>
          <a
            href="https://pratbot.hq.foocode.net:8443/docs"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-text-muted hover:text-text-secondary"
          >
            PratBot client API
          </a>
        </div>
        <div className="flex gap-2 border-b border-border pb-3">
          {CODE_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-sm uppercase transition-colors',
                activeTab === t
                  ? 'bg-synth-magenta text-text-on-accent'
                  : 'bg-surface-overlay text-text-secondary hover:bg-neutral-600',
              )}
            >
              {t}
            </button>
          ))}
        </div>
        <textarea
          value={codeValue}
          onChange={(e) => setCodeValue(e.target.value)}
          rows={16}
          spellCheck={false}
          placeholder={
            activeTab === 'js'
              ? "PratBot.on('follow', (e) => { /* e.username */ })\nPratBot.ready()"
              : activeTab === 'css'
                ? '#alert { font-family: sans-serif; }'
                : '<div id="alert"></div>'
          }
          className="input font-mono text-sm leading-relaxed"
        />
        <p className="text-xs text-text-muted">
          The injected <code className="text-text-secondary">PratBot</code> client delivers events
          (follow/subscribe/cheer/raid) to your JS. Reference uploaded assets by their URL.
        </p>
      </div>

      {/* Fields schema */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-text-primary">Fields</h3>
            <p className="text-xs text-text-muted">
              Streamer-tweakable settings, read in JS via{' '}
              <code className="text-text-secondary">PratBot.fields</code>.
            </p>
          </div>
          <button onClick={addField} className="btn-secondary flex items-center gap-2 text-sm">
            <Plus size={16} />
            Add field
          </button>
        </div>

        {fields.length === 0 ? (
          <p className="text-sm text-text-muted">No fields declared.</p>
        ) : (
          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs uppercase tracking-wide text-text-muted px-1">
              <span className="col-span-3">Key</span>
              <span className="col-span-2">Type</span>
              <span className="col-span-3">Label</span>
              <span className="col-span-3">Default</span>
              <span className="col-span-1" />
            </div>
            {fields.map((f, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input
                  className="input col-span-3 text-sm"
                  placeholder="accentColor"
                  value={f.key}
                  onChange={(e) => updateField(i, { key: e.target.value })}
                />
                <select
                  className="input col-span-2 text-sm"
                  value={f.type}
                  onChange={(e) => {
                    const type = e.target.value as FieldSpec['type']
                    updateField(i, { type, default: coerceDefault(type, String(f.default ?? '')) })
                  }}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  className="input col-span-3 text-sm"
                  placeholder="Accent colour"
                  value={f.label ?? ''}
                  onChange={(e) => updateField(i, { label: e.target.value })}
                />
                <input
                  className="input col-span-3 text-sm"
                  placeholder={f.type === 'boolean' ? 'true / false' : 'default'}
                  value={String(f.default ?? '')}
                  onChange={(e) => updateField(i, { default: coerceDefault(f.type, e.target.value) })}
                />
                <button
                  onClick={() => removeField(i)}
                  className="col-span-1 text-text-secondary hover:text-state-danger flex justify-center"
                  title="Remove field"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <p className="text-xs text-text-muted pt-1">
              Remember to <strong>Save</strong> after editing fields.
            </p>
          </div>
        )}
      </div>

      {/* Assets */}
      <AssetPanel channelId={chId} />

      {/* Preview + OBS */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-text-primary">Preview &amp; OBS</h3>
          <div className="flex items-center gap-2">
            <button onClick={mintToken} className="btn-secondary flex items-center gap-2 text-sm" disabled={minting}>
              {minting ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
              {overlay.has_token ? 'Regenerate token' : 'Mint token'}
            </button>
            {previewUrl && (
              <button
                onClick={() => setPreviewKey((k) => k + 1)}
                className="btn-secondary flex items-center gap-2 text-sm"
                title="Reload preview (after saving)"
              >
                <RefreshCw size={16} />
                Reload
              </button>
            )}
          </div>
        </div>

        {previewUrl ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-text-secondary">Test fire:</span>
              <select
                value={fireType}
                onChange={(e) => setFireType(e.target.value as FireType)}
                className="input py-1.5 text-sm w-auto"
              >
                {FIRE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button onClick={fire} className="btn-primary flex items-center gap-2 text-sm" disabled={firing}>
                {firing ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
                Fire {fireType}
              </button>
            </div>

            {/* Checkerboard backdrop so transparent overlays are visible. */}
            <div
              className="rounded-lg overflow-hidden border border-border"
              style={{
                backgroundColor: '#2a2a2a',
                backgroundImage:
                  'linear-gradient(45deg, #3a3a3a 25%, transparent 25%), linear-gradient(-45deg, #3a3a3a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #3a3a3a 75%), linear-gradient(-45deg, transparent 75%, #3a3a3a 75%)',
                backgroundSize: '24px 24px',
                backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0',
              }}
            >
              <iframe
                key={previewKey}
                src={previewUrl}
                title="overlay preview"
                className="w-full"
                style={{ height: 360, border: 0, background: 'transparent' }}
              />
            </div>
            <p className="text-xs text-text-muted">
              Preview shows the <strong>saved</strong> overlay. Save, hit Reload, then Fire. This is the
              real sandbox page — what renders here renders in OBS.
            </p>
          </>
        ) : (
          <p className="text-sm text-text-muted">
            Mint a token to preview this overlay and get its OBS browser-source URL.
          </p>
        )}
      </div>

      {tokenResult && <TokenModal result={tokenResult} onClose={() => setTokenResult(null)} />}
    </div>
  )
}
